import { useMemo, useState, useEffect } from 'react';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';
import { useHeaderData } from '@/hooks/useHeaderData';
import { supabase } from '@/integrations/supabase/client';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { VoiceTextarea } from '@/components/ui/voice-textarea';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Loader2, Search, Send, ChevronLeft, Building2, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { trackActivity } from '@/lib/activityTracking';
import { useFeatureRegistration, type FeatureMeta } from '@/contexts/FeatureRegistryContext';
import {
  useMessageCounterparties,
  type MessageCounterparty,
  type CounterpartyPerson,
} from '@/hooks/useMessageCounterparties';

export const FEATURE_META: FeatureMeta = {
  id: 'new-message',
  label: 'New Message',
  description:
    'Start a new conversation with a company you already work with, addressed to a specific person there',
  triggerType: 'dialog',
  personas: ['seller', 'buyer_seller', 'buyer'],
};

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Pre-select a company (from an access view row). */
  initialCompanyId?: string | null;
  /** Pre-select a person within that company. */
  initialUserId?: string | null;
  /** Optional starting text (e.g. a buy-request match reference). */
  initialDraft?: string | null;
  onSent?: () => void;
}

const displayName = (p: CounterpartyPerson) =>
  p.full_name?.trim() || p.email?.trim() || 'Unnamed';

const companyLabel = (c: MessageCounterparty) =>
  c.company_name?.trim() || 'Unnamed company';

export default function NewMessageDialog({
  open,
  onOpenChange,
  initialCompanyId,
  initialUserId,
  initialDraft,
  onSent,
}: Props) {
  useFeatureRegistration(FEATURE_META);
  const { user } = useAuth();
  const { data: headerData } = useHeaderData();
  const myCompanyId = headerData?.companyId ?? null;
  const { data: counterparties, isLoading } = useMessageCounterparties(open);

  const [query, setQuery] = useState('');
  const [companyId, setCompanyId] = useState<string | null>(initialCompanyId ?? null);
  const [personId, setPersonId] = useState<string | null>(initialUserId ?? null);
  const [content, setContent] = useState('');
  const [sending, setSending] = useState(false);

  // Reset to the caller's starting point every time the dialog opens.
  useEffect(() => {
    if (!open) return;
    setQuery('');
    setCompanyId(initialCompanyId ?? null);
    setPersonId(initialUserId ?? null);
    setContent(initialDraft ?? '');
  }, [open, initialCompanyId, initialUserId, initialDraft]);

  const selectedCompany = useMemo(
    () => counterparties?.find(c => c.company_id === companyId) ?? null,
    [counterparties, companyId]
  );

  // Default the person to the company's primary contact.
  useEffect(() => {
    if (!selectedCompany || personId) return;
    const preferred =
      selectedCompany.users.find(u => u.is_primary_contact) ?? selectedCompany.users[0];
    if (preferred) setPersonId(preferred.id);
  }, [selectedCompany, personId]);

  const filtered = useMemo(() => {
    const list = counterparties ?? [];
    const q = query.trim().toLowerCase();
    if (!q) return list;
    return list.filter(c => {
      if (companyLabel(c).toLowerCase().includes(q)) return true;
      return c.users.some(
        u =>
          displayName(u).toLowerCase().includes(q) ||
          (u.email ?? '').toLowerCase().includes(q)
      );
    });
  }, [counterparties, query]);

  const selectedPerson =
    selectedCompany?.users.find(u => u.id === personId) ?? null;

  const handleSend = async () => {
    if (!user || !myCompanyId || !selectedCompany || !content.trim()) return;
    setSending(true);
    try {
      const { data: inserted, error } = await supabase
        .from('platform_messages')
        .insert({
          sender_user_id: user.id,
          sender_company_id: myCompanyId,
          recipient_company_id: selectedCompany.company_id,
          content: content.trim(),
        })
        .select('id')
        .single();

      if (error || !inserted) {
        toast.error('Could not send', {
          description: error?.message?.includes('relationship')
            ? 'You can only message companies you already work with.'
            : error?.message || 'Please try again.',
        });
        return;
      }

      trackActivity('message_sent', {
        recipient_company_id: selectedCompany.company_id,
        origin: 'new_message',
      });

      toast.success('Message sent', {
        description: selectedPerson
          ? `Delivered to ${displayName(selectedPerson)} at ${companyLabel(selectedCompany)}`
          : `Delivered to ${companyLabel(selectedCompany)}`,
      });
      onOpenChange(false);
      onSent?.();
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>New message</DialogTitle>
          <DialogDescription>
            {selectedCompany
              ? companyLabel(selectedCompany)
              : 'Choose a company you work with, then the person to write to.'}
          </DialogDescription>
        </DialogHeader>

        {!selectedCompany ? (
          <div className="space-y-3">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="Search companies or people"
                aria-label="Search companies or people"
                className="pl-9 text-base md:text-sm"
              />
            </div>

            <div className="overlay-scroll max-h-[45vh] space-y-1 overflow-y-auto">
              {isLoading ? (
                <>
                  <Skeleton className="h-12 w-full" />
                  <Skeleton className="h-12 w-full" />
                  <Skeleton className="h-12 w-full" />
                </>
              ) : filtered.length === 0 ? (
                <p className="px-1 py-6 text-center text-sm text-muted-foreground">
                  {(counterparties?.length ?? 0) === 0
                    ? 'No companies yet. Share a menu or accept an invitation, then you can message them here.'
                    : 'No match.'}
                </p>
              ) : (
                filtered.map(c => (
                  <button
                    key={c.company_id}
                    type="button"
                    onClick={() => {
                      setCompanyId(c.company_id);
                      setPersonId(null);
                    }}
                    className="flex w-full items-center gap-3 rounded-md px-2 py-2 text-left hover:bg-muted"
                  >
                    <span className="flex circle-9 shrink-0 items-center justify-center bg-primary/10 text-primary">
                      <Building2 className="h-4 w-4" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium">
                        {companyLabel(c)}
                      </span>
                      <span className="block truncate text-xs text-muted-foreground">
                        {c.users.length === 0
                          ? 'No people yet'
                          : `${c.users.length} ${c.users.length === 1 ? 'person' : 'people'}`}
                      </span>
                    </span>
                  </button>
                ))
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="-ml-2 h-8 px-2 text-xs"
              onClick={() => {
                setCompanyId(null);
                setPersonId(null);
              }}
            >
              <ChevronLeft className="mr-1 h-3.5 w-3.5" />
              Choose a different company
            </Button>

            <div className="space-y-2">
              <Label>Send to</Label>
              {selectedCompany.users.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No one has joined this company yet. Your message still reaches the company.
                </p>
              ) : (
                <div className="overlay-scroll max-h-40 space-y-1 overflow-y-auto">
                  {selectedCompany.users.map(p => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => setPersonId(p.id)}
                      className={cn(
                        'flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-sm hover:bg-muted',
                        personId === p.id && 'bg-muted'
                      )}
                    >
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center gap-2">
                          <span className="truncate font-medium">{displayName(p)}</span>
                          {p.is_primary_contact && (
                            <Badge variant="secondary" className="h-4 px-1.5 text-xs">
                              Primary contact
                            </Badge>
                          )}
                        </span>
                        {p.email && (
                          <span className="block truncate text-xs text-muted-foreground">
                            {p.email}
                          </span>
                        )}
                      </span>
                      {personId === p.id && <Check className="h-4 w-4 shrink-0 text-primary" />}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="new-msg-body">Your message</Label>
              <VoiceTextarea onValueChange={setContent}
                id="new-msg-body"
                rows={6}
                value={content}
                onChange={e => setContent(e.target.value)}
                placeholder={
                  selectedPerson
                    ? `Hi ${displayName(selectedPerson).split(' ')[0]},`
                    : 'Write your message'
                }
                className="text-base md:text-sm"
              />
              <p className="text-xs text-muted-foreground">
                Everyone at {companyLabel(selectedCompany)} can see this conversation.
              </p>
            </div>

            <div className="flex justify-end gap-2">
              <Button type="button" variant="quiet" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="button" onClick={handleSend} disabled={sending || !content.trim()}>
                {sending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Send className="mr-2 h-4 w-4" />
                )}
                Send
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
